import * as Yup from "yup";
import "yup-phone";

export const GenderOptions = ["", "Male", "Female", "Other"] as const;
export const SpeciesOptions = ["", "Dog", "Cat", "Fish", "Other"] as const;
export const PhoneOptions = ["Work Mobile", "Personal Mobile", "Home"] as const;
export const MarriedOptions = ["", "Married", "Single"] as const;
export const LivingOptions = ["Living", "Deceased"] as const;

const clearIfFalse = (value: any) => (value ? value : undefined);
const filterEmpty = (value: string) => value !== "";
const phoneSchema = Yup.string().phone("US", false, "Invalid Phone Number");

export const MemberSchema = Yup.object({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string()
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
  birthDate: Yup.date().required("Required").min(new Date(1900, 0, 1)),
  deceasedDate: Yup.date().notRequired().min(new Date(1900, 0, 1)),
  hireDate: Yup.date().notRequired().min(new Date(1900, 0, 1)),
  marriedDate: Yup.date().notRequired().min(new Date(1900, 0, 1)),
  gender: Yup.string()
    .transform(clearIfFalse)
    .oneOf([...GenderOptions].filter(filterEmpty)),
  phoneDropdown: Yup.mixed()
    .transform(clearIfFalse)
    .notRequired()
    .oneOf([...PhoneOptions].filter(filterEmpty)),
  maritalStatus: Yup.mixed()
    .oneOf([...MarriedOptions].filter(filterEmpty))
    .defined("Required"),
  phone: Yup.string()
    .transform(clearIfFalse)
    .notRequired()
    .test("test-phone", "Invalid Phone Number", value => {
      if (value) return phoneSchema.isValidSync(value);
      return true;
    })
    .transform(val => val.replace(/[^\d]/g, "")),
  avatarImage: Yup.mixed().notRequired(),
  living: Yup.string().notRequired(),
});

export const SpouseSchema = Yup.object({
  firstName: Yup.string()
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  lastName: Yup.string()
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
  birthDate: Yup.date().required("Required").min(new Date(1900, 0, 1)),
  deceasedDate: Yup.date().notRequired().min(new Date(1900, 0, 1)),
  gender: Yup.string()
    .transform(clearIfFalse)
    .oneOf([...GenderOptions].filter(filterEmpty)),
  phoneDropdown: Yup.mixed().oneOf([...PhoneOptions]),
  phone: Yup.string()
    .transform(clearIfFalse)
    .notRequired()
    .test("test-phone", "Invalid Phone Number", value => {
      if (value) return phoneSchema.isValidSync(value);
      return true;
    })
    .transform(val => val.replace(/[^\d]/g, "")),
  avatarImage: Yup.mixed().notRequired(),
  living: Yup.string(),
});

export const ChildSchema = Yup.object({
  firstName: Yup.string()
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  lastName: Yup.string()
    .transform(clearIfFalse)
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .notRequired(),
  email: Yup.string()
    .transform(clearIfFalse)
    .email("Invalid email")
    .notRequired(),
  phone: Yup.string().transform(clearIfFalse).notRequired(),
  birthDate: Yup.date().required("Required").min(new Date(1900, 0, 1)),
  deceasedDate: Yup.date().notRequired().min(new Date(1900, 0, 1)),
  gender: Yup.mixed()
    .transform(clearIfFalse)
    .oneOf([...GenderOptions].filter(filterEmpty)),
  living: Yup.string(),
});

export const PetSchema = Yup.object({
  firstName: Yup.string()
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  lastName: Yup.string()
    .transform(clearIfFalse)
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .notRequired(),
  email: Yup.string()
    .transform(clearIfFalse)
    .email("Invalid email")
    .notRequired(),
  phone: Yup.string().transform(clearIfFalse).notRequired(),
  birthDate: Yup.date().required("Required").min(new Date(1900, 0, 1)),
  deceasedDate: Yup.date().notRequired().min(new Date(1900, 0, 1)),
  species: Yup.mixed().oneOf([...SpeciesOptions].filter(filterEmpty)),
  living: Yup.string(),
});

export const PersonalInfoSchema = Yup.object({
  member: MemberSchema,
  spouse: Yup.array().of(SpouseSchema).max(1),
  children: Yup.array().of(ChildSchema),
  pets: Yup.array().of(PetSchema),
  associateId: Yup.string(),
});

export type PersonalInformation = Yup.TypeOf<typeof PersonalInfoSchema>;
export type Member = Yup.TypeOf<typeof MemberSchema>;
export type Spouse = Yup.TypeOf<typeof SpouseSchema>;
export type Child = Yup.TypeOf<typeof ChildSchema>;
export type Pet = Yup.TypeOf<typeof PetSchema>;
